// Tasks fetch helpers split from the legacy mega-file.
import { sql } from "../../base";
import { getTasksSchemaInfo } from "../../schemaCache";
import { ensureDeferredTasksAreFresh } from "./deferred";
import type { Task, TaskWithContext } from "../../types";

export const fetchTasks = {
  async findByUserId(userId: string): Promise<Task[]> {
      try {
        // Make sure deferred tasks are revived before we list them.
        await ensureDeferredTasksAreFresh(userId);
        const result = await sql`
          SELECT * FROM tasks
          WHERE user_id = ${userId} OR assigned_to = ${userId}
          ORDER BY position ASC, created_at DESC
        `;
        return (result || []) as Task[];
      } catch (error) {
        console.error('Error fetching tasks by user:', error);
        return [];
      }
    },

  async findById(id: string, userId: string): Promise<Task | null> {
      try {
        // Sync deferred tasks so a bumped item does not stay hidden in detail views.
        await ensureDeferredTasksAreFresh(userId);
        const result = await sql`
          SELECT * FROM tasks
          WHERE id = ${id} AND (user_id = ${userId} OR assigned_to = ${userId})
          LIMIT 1
        `;
        return result[0] as Task || null;
      } catch (error) {
        console.error('Error fetching task by id:', error);
        return null;
      }
    },

  async findRegularTasks(userId: string): Promise<Task[]> {
      try {
        // Keep deferred regular tasks in sync with the new day.
        await ensureDeferredTasksAreFresh(userId);
        // Get cached schema information
        const schema = await getTasksSchemaInfo();
        const hasRegular = schema.hasRegular;
        const hasDaily = schema.hasDaily;
        
        if (!hasRegular && !hasDaily) {
          console.warn('Regular tasks feature requires migration to be applied');
          return [];
        }
        
        // Use is_regular if available, fall back to is_daily for backward compatibility
        if (hasRegular) {
          const result = await sql`
            WITH ranked AS (
              SELECT *, ROW_NUMBER() OVER (PARTITION BY id ORDER BY position ASC, created_at DESC) AS rn
              FROM tasks
              WHERE (user_id = ${userId} OR assigned_to = ${userId})
              AND is_regular = 1
            )
            SELECT * FROM ranked WHERE rn = 1
            ORDER BY position ASC, created_at DESC
          `;
          return (result || []) as Task[];
        } else {
          const result = await sql`
            WITH ranked AS (
              SELECT *, ROW_NUMBER() OVER (PARTITION BY id ORDER BY position ASC, created_at DESC) AS rn
              FROM tasks
              WHERE (user_id = ${userId} OR assigned_to = ${userId})
              AND is_daily = 1
            )
            SELECT * FROM ranked WHERE rn = 1
            ORDER BY position ASC, created_at DESC
          `;
          return (result || []) as Task[];
        }
      } catch (error) {
        console.error('Error fetching regular tasks:', error);
        return [];
      }
    },

  async findRegularTaskTemplates(userId: string): Promise<Task[]> {
      try {
        await ensureDeferredTasksAreFresh(userId);
        console.log('[findRegularTaskTemplates] Looking for templates for user:', userId);
        
        // Get unique regular task templates - one per unique combination of title, description, frequency, and priority
        // Get the OLDEST instance of each unique regular task (the original template)
        const result = await sql`
          WITH latest_tasks AS (
            SELECT DISTINCT ON (LOWER(TRIM(title)), LOWER(TRIM(COALESCE(description, ''))), frequency, priority)
              *
            FROM tasks
            WHERE (user_id = ${userId} OR assigned_to = ${userId})
            AND is_regular = true
            ORDER BY LOWER(TRIM(title)), LOWER(TRIM(COALESCE(description, ''))), frequency, priority, 
                     created_at ASC  -- Get the OLDEST instance (original template)
          )
          SELECT * FROM latest_tasks
          ORDER BY position ASC, created_at ASC
        `;
        
        console.log('[findRegularTaskTemplates] Query results:', {
          templateCount: result.length,
          templates: result.map((t: any) => ({
            id: t.id,
            title: t.title,
            start_date: t.start_date,
            next_due_date: t.next_due_date,
            frequency: t.frequency,
            status: t.status,
            created_at: t.created_at
          }))
        });
        
        return (result || []) as Task[];
      } catch (error) {
        console.error('Error fetching regular task templates:', error);
        return [];
      }
    },

  async findActiveRegularTasks(userId: string): Promise<Task[]> {
      try {
        await ensureDeferredTasksAreFresh(userId);
        // Find all incomplete regular tasks (these are the active instances)
        const result = await sql`
          WITH ranked AS (
            SELECT *, ROW_NUMBER() OVER (PARTITION BY id ORDER BY 
              CASE priority
                WHEN 'Critical' THEN 1
                WHEN 'High' THEN 2
                WHEN 'Medium' THEN 3
                WHEN 'Low' THEN 4
                ELSE 5
              END ASC,
              COALESCE(next_due_date, start_date) ASC,
              position ASC
            ) AS rn
            FROM tasks
            WHERE (user_id = ${userId} OR assigned_to = ${userId})
            AND is_regular = 1
            AND status != 'Complete'
          )
          SELECT * FROM ranked WHERE rn = 1
          ORDER BY 
            CASE priority
              WHEN 'Critical' THEN 1
              WHEN 'High' THEN 2
              WHEN 'Medium' THEN 3
              WHEN 'Low' THEN 4
              ELSE 5
            END ASC,
            COALESCE(next_due_date, start_date) ASC,
            position ASC
        `;
        return (result || []) as Task[];
      } catch (error) {
        console.error('Error fetching active regular tasks:', error);
        return [];
      }
    },

  async findTodaysRegularTasks(userId: string): Promise<Task[]> {
      try {
        await ensureDeferredTasksAreFresh(userId);
        // Create today's date at UTC midnight
        const now = new Date();
        const today = new Date(Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          0, 0, 0, 0
        ));
        
        // Debug logging disabled in production
        // Uncomment for debugging:
        // console.log('[findTodaysRegularTasks] Query parameters:', {
        //   userId: userId,
        //   now: now.toISOString(),
        //   today: today.toISOString(),
        //   todayDate: today.toDateString(),
        //   timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        // });
        
        // Get today's date as a string for consistent comparison
        const todayString = today.toISOString().split('T')[0];
        
        const result = await sql`
          WITH ranked AS (
            SELECT *, ROW_NUMBER() OVER (PARTITION BY id ORDER BY 
              CASE priority
                WHEN 'Critical' THEN 1
                WHEN 'High' THEN 2
                WHEN 'Medium' THEN 3
                WHEN 'Low' THEN 4
                ELSE 5
              END ASC,
              position ASC,
              created_at DESC
            ) AS rn
            FROM tasks
            WHERE (user_id = ${userId} OR assigned_to = ${userId})
            AND is_regular = 1
            AND status != 'Complete'
            AND status != 'Superseded'
            AND (
              (next_due_date IS NOT NULL AND DATE(next_due_date) = ${todayString})
              OR (next_due_date IS NULL AND DATE(start_date) = ${todayString})
            )
          )
          SELECT * FROM ranked WHERE rn = 1
          ORDER BY 
            CASE priority
              WHEN 'Critical' THEN 1
              WHEN 'High' THEN 2
              WHEN 'Medium' THEN 3
              WHEN 'Low' THEN 4
              ELSE 5
            END ASC,
            position ASC,
            created_at DESC
        `;
        
        // Debug logging disabled in production
        // Uncomment for debugging:
        // console.log('[findTodaysRegularTasks] Query results:', {
        //   taskCount: result.length,
        //   tasks: result.map((t: any) => ({
        //     id: t.id,
        //     title: t.title,
        //     start_date: t.start_date,
        //     next_due_date: t.next_due_date,
        //     frequency: t.frequency,
        //     status: t.status
        //   }))
        // });
        
        return (result || []) as Task[];
      } catch (error) {
        console.error('Error fetching today\'s regular tasks:', error);
        return [];
      }
    },

  async findOverdueRegularTasks(userId: string): Promise<Task[]> {
      try {
        await ensureDeferredTasksAreFresh(userId);
        // Create today's date at UTC midnight
        const now = new Date();
        const today = new Date(Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          0, 0, 0, 0
        ));
        
        // Get today's date as a string for consistent comparison
        const todayString = today.toISOString().split('T')[0];
        
        const result = await sql`
          WITH ranked AS (
            SELECT *, ROW_NUMBER() OVER (PARTITION BY id ORDER BY 
              CASE priority
                WHEN 'Critical' THEN 1
                WHEN 'High' THEN 2
                WHEN 'Medium' THEN 3
                WHEN 'Low' THEN 4
                ELSE 5
              END ASC,
              COALESCE(next_due_date, start_date) ASC,
              position ASC
            ) AS rn
            FROM tasks
            WHERE (user_id = ${userId} OR assigned_to = ${userId})
            AND is_regular = 1
            AND status != 'Complete'
            AND (
              (next_due_date IS NOT NULL AND DATE(next_due_date) < ${todayString})
              OR (next_due_date IS NULL AND DATE(start_date) < ${todayString})
            )
          )
          SELECT * FROM ranked WHERE rn = 1
          ORDER BY 
            CASE priority
              WHEN 'Critical' THEN 1
              WHEN 'High' THEN 2
              WHEN 'Medium' THEN 3
              WHEN 'Low' THEN 4
              ELSE 5
            END ASC,
            COALESCE(next_due_date, start_date) ASC,
            position ASC
        `;
        return (result || []) as Task[];
      } catch (error) {
        console.error('Error fetching overdue regular tasks:', error);
        return [];
      }
    },

  async getTodaysProgress(userId: string): Promise<{ total: number; completed: number }> {
      try {
        await ensureDeferredTasksAreFresh(userId);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const result = await sql`
          SELECT 
            COUNT(*) FILTER (WHERE status = 'Complete' AND end_date >= ${today} AND end_date < ${tomorrow}) as completed,
            COUNT(*) as total
          FROM tasks
          WHERE (user_id = ${userId} OR assigned_to = ${userId})
          AND is_regular = true
          AND (
            -- Tasks due today (not yet completed)
            (status != 'Complete' AND (
              (next_due_date IS NOT NULL AND next_due_date = ${today})
              OR (next_due_date IS NULL AND start_date = ${today})
            ))
            OR
            -- Tasks completed today
            (status = 'Complete' AND end_date >= ${today} AND end_date < ${tomorrow})
          )
        `;

        return {
          total: parseInt(result[0]?.total || '0'),
          completed: parseInt(result[0]?.completed || '0')
        };
      } catch (error) {
        console.error("Error getting today's progress:", error);
        return { total: 0, completed: 0 };
      }
    },

  // Keep findDailyTasks as an alias for backward compatibility
  async findDailyTasks(userId: string): Promise<Task[]> {
      return this.findRegularTasks(userId);
    },

  async findByGoalId(goalId: string, userId: string): Promise<Task[]> {
      try {
        await ensureDeferredTasksAreFresh(userId);
        const result = await sql`
          SELECT * FROM tasks
          WHERE goal_id = ${goalId} AND (user_id = ${userId} OR assigned_to = ${userId})
          ORDER BY position ASC, created_at DESC
        `;
        return (result || []) as Task[];
      } catch (error) {
        console.error('Error fetching tasks by goal:', error);
        return [];
      }
    },

  async findTasksWithContext(userId: string): Promise<TaskWithContext[]> {
      try {
        await ensureDeferredTasksAreFresh(userId);
        const result = await sql`
          SELECT 
            t.*,
            g.id as goal_id,
            g.title as goal_title,
            g.description as goal_description,
            g.matter_id,
            g.project_id,
            g.client_id as goal_client_id,
            g.deadline as goal_deadline,
            m.id as matter_id,
            m.title as matter_title,
            p.id as project_id,
            p.title as project_title,
            COALESCE(m.client_id, p.client_id, g.client_id) as client_id,
            c.name as client_name
          FROM tasks t
          LEFT JOIN goals g ON t.goal_id = g.id
          LEFT JOIN matters m ON g.matter_id = m.id
          LEFT JOIN projects p ON g.project_id = p.id
          LEFT JOIN clients c ON COALESCE(m.client_id, p.client_id, g.client_id) = c.id
          WHERE (t.user_id = ${userId} OR t.assigned_to = ${userId})
          AND (t.is_regular = true OR t.is_live = true)
          AND t.status NOT IN ('Complete', 'Superseded')
          ORDER BY t.position ASC, t.created_at DESC
        `;
        
        return (result || []).map(row => ({
          ...row,
          goal: row.goal_id ? {
            id: row.goal_id,
            title: row.goal_title,
            description: row.goal_description,
            matter_id: row.matter_id,
            project_id: row.project_id,
            client_id: row.goal_client_id,
            deadline: row.goal_deadline
          } : undefined,
          matter: row.matter_id ? {
            id: row.matter_id,
            title: row.matter_title
          } : undefined,
          project: row.project_id ? {
            id: row.project_id,
            title: row.project_title
          } : undefined,
          client: row.client_id ? {
            id: row.client_id,
            name: row.client_name
          } : undefined
        })) as TaskWithContext[];
      } catch (error) {
        console.error('Error fetching tasks with context:', error);
        return [];
      }
    },

  async findTodaysRegularTasksWithContext(userId: string): Promise<TaskWithContext[]> {
      try {
        await ensureDeferredTasksAreFresh(userId);
        // Create today's date at UTC midnight
        const now = new Date();
        const today = new Date(Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          0, 0, 0, 0
        ));
        
        // Get today's date as a string for consistent comparison
        const todayString = today.toISOString().split('T')[0];
        
        const result = await sql`
          WITH ranked AS (
            SELECT 
              t.*,
              g.id as goal_id,
              g.title as goal_title,
              g.description as goal_description,
              g.matter_id,
              g.project_id,
              g.client_id as goal_client_id,
              g.deadline as goal_deadline,
              m.id as matter_id,
              m.title as matter_title,
              p.id as project_id,
              p.title as project_title,
              COALESCE(m.client_id, p.client_id, g.client_id) as client_id,
              c.name as client_name,
              ROW_NUMBER() OVER (PARTITION BY t.id ORDER BY 
                CASE t.priority
                  WHEN 'Critical' THEN 1
                  WHEN 'High' THEN 2
                  WHEN 'Medium' THEN 3
                  WHEN 'Low' THEN 4
                  ELSE 5
                END ASC,
                t.position ASC,
                t.created_at DESC
              ) AS rn
            FROM tasks t
            LEFT JOIN goals g ON t.goal_id = g.id
            LEFT JOIN matters m ON g.matter_id = m.id
            LEFT JOIN projects p ON g.project_id = p.id
            LEFT JOIN clients c ON COALESCE(m.client_id, p.client_id, g.client_id) = c.id
            WHERE (t.user_id = ${userId} OR t.assigned_to = ${userId})
            AND t.is_regular = 1
            AND t.status != 'Complete'
            AND t.status != 'Superseded'
            AND (
              (t.next_due_date IS NOT NULL AND DATE(t.next_due_date) = ${todayString})
              OR (t.next_due_date IS NULL AND DATE(t.start_date) = ${todayString})
            )
          )
          SELECT * FROM ranked WHERE rn = 1
          ORDER BY 
            CASE priority
              WHEN 'Critical' THEN 1
              WHEN 'High' THEN 2
              WHEN 'Medium' THEN 3
              WHEN 'Low' THEN 4
              ELSE 5
            END ASC,
            position ASC,
            created_at DESC
        `;
        
        return (result || []).map(row => ({
          ...row,
          goal: row.goal_id ? {
            id: row.goal_id,
            title: row.goal_title,
            description: row.goal_description,
            matter_id: row.matter_id,
            project_id: row.project_id,
            client_id: row.goal_client_id,
            deadline: row.goal_deadline
          } : undefined,
          matter: row.matter_id ? {
            id: row.matter_id,
            title: row.matter_title
          } : undefined,
          project: row.project_id ? {
            id: row.project_id,
            title: row.project_title
          } : undefined,
          client: row.client_id ? {
            id: row.client_id,
            name: row.client_name
          } : undefined
        })) as TaskWithContext[];
      } catch (error) {
        console.error('Error fetching today\'s regular tasks with context:', error);
        return [];
      }
    },

  async findOverdueRegularTasksWithContext(userId: string): Promise<TaskWithContext[]> {
      try {
        await ensureDeferredTasksAreFresh(userId);
        // Create today's date at UTC midnight
        const now = new Date();
        const today = new Date(Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          0, 0, 0, 0
        ));
        
        // Get today's date as a string for consistent comparison
        const todayString = today.toISOString().split('T')[0];
        
        const result = await sql`
          WITH ranked AS (
            SELECT 
              t.*,
              g.id as goal_id,
              g.title as goal_title,
              g.description as goal_description,
              g.matter_id,
              g.project_id,
              g.client_id as goal_client_id,
              g.deadline as goal_deadline,
              m.id as matter_id,
              m.title as matter_title,
              p.id as project_id,
              p.title as project_title,
              COALESCE(m.client_id, p.client_id, g.client_id) as client_id,
              c.name as client_name,
              ROW_NUMBER() OVER (PARTITION BY t.id ORDER BY 
                CASE t.priority
                  WHEN 'Critical' THEN 1
                  WHEN 'High' THEN 2
                  WHEN 'Medium' THEN 3
                  WHEN 'Low' THEN 4
                  ELSE 5
                END ASC,
                COALESCE(t.next_due_date, t.start_date) ASC,
                t.position ASC
              ) AS rn
            FROM tasks t
            LEFT JOIN goals g ON t.goal_id = g.id
            LEFT JOIN matters m ON g.matter_id = m.id
            LEFT JOIN projects p ON g.project_id = p.id
            LEFT JOIN clients c ON COALESCE(m.client_id, p.client_id, g.client_id) = c.id
            WHERE (t.user_id = ${userId} OR t.assigned_to = ${userId})
            AND t.is_regular = 1
            AND t.status != 'Complete'
            AND t.status != 'Superseded'
            AND (
              (t.next_due_date IS NOT NULL AND DATE(t.next_due_date) < ${todayString})
              OR (t.next_due_date IS NULL AND DATE(t.start_date) < ${todayString})
            )
          )
          SELECT * FROM ranked WHERE rn = 1
          ORDER BY 
            CASE priority
              WHEN 'Critical' THEN 1
              WHEN 'High' THEN 2
              WHEN 'Medium' THEN 3
              WHEN 'Low' THEN 4
              ELSE 5
            END ASC,
            COALESCE(next_due_date, start_date) ASC,
            position ASC
        `;
        
        return (result || []).map(row => ({
          ...row,
          goal: row.goal_id ? {
            id: row.goal_id,
            title: row.goal_title,
            description: row.goal_description,
            matter_id: row.matter_id,
            project_id: row.project_id,
            client_id: row.goal_client_id,
            deadline: row.goal_deadline
          } : undefined,
          matter: row.matter_id ? {
            id: row.matter_id,
            title: row.matter_title
          } : undefined,
          project: row.project_id ? {
            id: row.project_id,
            title: row.project_title
          } : undefined,
          client: row.client_id ? {
            id: row.client_id,
            name: row.client_name
          } : undefined
        })) as TaskWithContext[];
      } catch (error) {
        console.error('Error fetching overdue regular tasks with context:', error);
        return [];
      }
    },
};
